import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { motion } from 'framer-motion';
import axios from 'axios';
import {
  Sparkles, Search, Filter, Plus, MapPin, MessageCircle, CheckCircle,
  Heart, User, Trash2, Pencil, X, Camera
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import ShareButtons from '../components/ShareButtons';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const WishCard = ({ wish, language, onContact, onEdit, onDelete, isAuthenticated, currentUserId }) => {
  const isOwner = currentUserId === wish.user_id;
  const it = language === 'it';
  const shareUrl = `${API_URL}/api/share/wish/${wish.wish_id}`;

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
      className="bg-white dark:bg-stone-800 rounded-2xl shadow-sm border border-stone-200 dark:border-stone-700 overflow-hidden hover:shadow-md transition-shadow"
      data-testid={`wish-card-${wish.wish_id}`}>
      {/* Cover image */}
      {wish.images?.length > 0 && (
        <Link to={`/heart-desires/${wish.wish_id}`}>
        <div className="aspect-[3/1] overflow-hidden bg-stone-100 relative">
          <img src={wish.images[0]} alt={wish.title} className="w-full h-full object-cover" />
          {isOwner && (
            <div className="absolute top-2 right-2 flex gap-1.5">
              <button onClick={() => onEdit(wish)} data-testid={`edit-wish-${wish.wish_id}`}
                className="p-2 bg-white/90 backdrop-blur-sm rounded-full shadow hover:bg-white transition-colors">
                <Pencil className="h-3.5 w-3.5 text-stone-600" />
              </button>
              <button onClick={() => onDelete(wish)} data-testid={`delete-wish-${wish.wish_id}`}
                className="p-2 bg-white/90 backdrop-blur-sm rounded-full shadow hover:bg-red-50 transition-colors">
                <Trash2 className="h-3.5 w-3.5 text-red-500" />
              </button>
            </div>
          )}
        </div>
        </Link>
      )}
      <div className="p-5">
        <div className="flex items-start gap-4">
          <Link to={`/profile/${wish.user_id}`}>
            <Avatar className="h-12 w-12 shrink-0 ring-2 ring-rose-100 dark:ring-rose-900">
              <AvatarImage src={wish.user_picture || `https://api.dicebear.com/9.x/lorelei/svg?seed=${wish.user_id}`} />
              <AvatarFallback className="bg-rose-100 text-rose-700">{wish.user_name?.[0]}</AvatarFallback>
            </Avatar>
          </Link>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <Link to={`/profile/${wish.user_id}`} className="font-semibold text-stone-900 dark:text-white hover:text-rose-700 transition-colors">
                {wish.user_name}
              </Link>
              {wish.is_verified && <CheckCircle className="h-4 w-4 text-blue-500" />}
            </div>
            <div className="flex items-center gap-1.5 flex-wrap mb-2">
              <Badge className="bg-rose-100 text-rose-700 dark:bg-rose-900 dark:text-rose-300 text-xs">
                <Sparkles className="h-3 w-3 mr-0.5" />{wish.category_name || wish.category}
              </Badge>
              {wish.city && (
                <Badge variant="outline" className="text-xs text-stone-500 dark:text-stone-400">
                  <MapPin className="h-3 w-3 mr-0.5" />{wish.city}
                </Badge>
              )}
            </div>
            <Link to={`/heart-desires/${wish.wish_id}`}>
              <h3 className="font-heading font-semibold text-stone-900 dark:text-white text-base mb-1 hover:text-rose-700 transition-colors">{wish.title}</h3>
            </Link>
            <p className="text-stone-500 dark:text-stone-400 text-sm line-clamp-3 mb-3">{wish.description}</p>

            <div className="flex items-center gap-2 flex-wrap">
              {isAuthenticated && !isOwner && (
                <Button size="sm" onClick={() => onContact(wish)} data-testid={`contact-wish-${wish.wish_id}`}
                  className="bg-rose-600 hover:bg-rose-700 text-white rounded-full text-xs px-4">
                  <MessageCircle className="h-3.5 w-3.5 mr-1" />{it ? 'Realizza il desiderio' : 'Fulfill wish'}
                </Button>
              )}
              {isOwner && !wish.images?.length && (
                <>
                  <Button size="sm" variant="ghost" onClick={() => onEdit(wish)} data-testid={`edit-wish-${wish.wish_id}`}
                    className="text-stone-500 hover:bg-stone-100 dark:hover:bg-stone-700 rounded-full text-xs">
                    <Pencil className="h-3.5 w-3.5 mr-1" />{it ? 'Modifica' : 'Edit'}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => onDelete(wish)} data-testid={`delete-wish-${wish.wish_id}`}
                    className="text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-full text-xs">
                    <Trash2 className="h-3.5 w-3.5 mr-1" />{it ? 'Elimina' : 'Delete'}
                  </Button>
                </>
              )}
              <div className="ml-auto scale-75 origin-right">
                <ShareButtons url={shareUrl} title={wish.title} description={wish.description} language={language} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const WishForm = ({ language, token, categories, onSuccess, editItem }) => {
  const [title, setTitle] = useState(editItem?.title || '');
  const [description, setDescription] = useState(editItem?.description || '');
  const [category, setCategory] = useState(editItem?.category || '');
  const [city, setCity] = useState(editItem?.city || '');
  const [images, setImages] = useState(editItem?.images || []);
  const [loading, setLoading] = useState(false);
  const imageInputRef = useRef(null);
  const it = language === 'it';
  const isEditing = !!editItem;

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    if (images.length + files.length > 5) { toast.error(it ? 'Massimo 5 immagini' : 'Maximum 5 images'); return; }
    files.forEach(file => {
      if (file.size > 5 * 1024 * 1024) return;
      const reader = new FileReader();
      reader.onloadend = () => setImages(prev => [...prev, reader.result]);
      reader.readAsDataURL(file);
    });
  };

  const handleSubmit = async () => {
    if (!title.trim() || !description.trim()) {
      toast.error(it ? 'Compila tutti i campi obbligatori' : 'Fill all required fields');
      return;
    }
    if (images.length === 0) {
      toast.error(it ? 'Aggiungi almeno un\'immagine di copertina' : 'Add at least one cover image');
      return;
    }
    setLoading(true);
    try {
      const data = { title: title.trim(), description: description.trim(), category: category || 'other', city: city.trim(), images };
      if (isEditing) {
        await axios.put(`${API_URL}/api/wishes/${editItem.wish_id}`, data, { headers: { Authorization: `Bearer ${token}` } });
        toast.success(it ? 'Desiderio aggiornato!' : 'Wish updated!');
      } else {
        await axios.post(`${API_URL}/api/wishes`, data, { headers: { Authorization: `Bearer ${token}` } });
        toast.success(it ? 'Desiderio pubblicato!' : 'Wish published!');
      }
      onSuccess();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
    finally { setLoading(false); }
  };

  return (
    <div className="space-y-5">
      <div className="space-y-2">
        <Label className="font-semibold">{it ? 'Il tuo desiderio *' : 'Your wish *'}</Label>
        <Input data-testid="wish-title-input" value={title} onChange={e => setTitle(e.target.value)}
          placeholder={it ? 'Es: Vorrei imparare a suonare la chitarra' : 'E.g.: I wish I could learn guitar'}
          className="h-12 rounded-xl" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2">
          <Label className="font-semibold">{it ? 'Categoria' : 'Category'}</Label>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger data-testid="wish-category-select" className="h-12 rounded-xl">
              <SelectValue placeholder={it ? 'Seleziona' : 'Select'} />
            </SelectTrigger>
            <SelectContent>
              {categories.map(c => (
                <SelectItem key={c.id} value={c.id}>{it ? c.name_it : c.name_en}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label className="font-semibold"><MapPin className="h-3.5 w-3.5 inline mr-1" />{it ? 'Città' : 'City'}</Label>
          <Input data-testid="wish-city-input" value={city} onChange={e => setCity(e.target.value)}
            placeholder={it ? 'Es: Roma' : 'E.g.: Rome'} className="h-12 rounded-xl" />
        </div>
      </div>
      <div className="space-y-2">
        <Label className="font-semibold">{it ? 'Racconta il tuo desiderio *' : 'Tell your wish *'}</Label>
        <Textarea data-testid="wish-description-input" value={description} onChange={e => setDescription(e.target.value)}
          placeholder={it ? 'Spiega perché questo desiderio è importante per te...' : 'Explain why this wish matters to you...'}
          className="min-h-[120px] rounded-xl" />
      </div>
      {/* Photos */}
      <div className="space-y-2">
        <Label className="font-semibold">{it ? 'Foto di copertina * (max 5)' : 'Cover photos * (max 5)'}</Label>
        <input type="file" accept="image/*" multiple onChange={handleImageUpload} className="hidden" ref={imageInputRef} />
        <div className="flex flex-wrap gap-3">
          {images.map((img, i) => (
            <div key={i} className="relative group">
              <img src={img} alt="" className="h-20 w-20 object-cover rounded-xl border-2 border-stone-200" />
              <button type="button" onClick={() => setImages(prev => prev.filter((_, idx) => idx !== i))}
                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
          {images.length < 5 && (
            <button type="button" onClick={() => imageInputRef.current?.click()}
              className="h-20 w-20 rounded-xl border-2 border-dashed border-stone-300 flex flex-col items-center justify-center text-stone-400 hover:border-rose-500 hover:text-rose-600 transition-colors">
              <Camera className="h-5 w-5 mb-1" /><span className="text-[10px]">{it ? 'Aggiungi' : 'Add'}</span>
            </button>
          )}
        </div>
      </div>
      <Button onClick={handleSubmit} disabled={loading} data-testid="wish-publish-btn"
        className="w-full h-12 bg-rose-600 hover:bg-rose-700 text-white rounded-xl font-semibold">
        {loading ? '...' : isEditing ? (it ? 'Salva modifiche' : 'Save changes') : (it ? 'Pubblica desiderio' : 'Publish wish')}
      </Button>
    </div>
  );
};

const HeartDesires = () => {
  const { isAuthenticated, user, token } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const highlightItemId = searchParams.get('item');
  const [wishes, setWishes] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [cityFilter, setCityFilter] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showCreate, setShowCreate] = useState(false);
  const [editingWish, setEditingWish] = useState(null);
  const it = language === 'it';

  useEffect(() => { fetchCategories(); }, []);
  useEffect(() => { fetchWishes(); }, [selectedCategory]);

  useEffect(() => {
    if (highlightItemId && !loading) {
      setTimeout(() => {
        const el = document.querySelector(`[data-testid="wish-card-${highlightItemId}"]`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          el.classList.add('ring-2', 'ring-rose-500', 'ring-offset-2');
          setTimeout(() => el.classList.remove('ring-2', 'ring-rose-500', 'ring-offset-2'), 4000);
        }
      }, 300);
    }
  }, [highlightItemId, loading]);

  const fetchCategories = async () => {
    try { const res = await axios.get(`${API_URL}/api/wishes/categories`); setCategories(res.data.categories); } catch {}
  };

  const fetchWishes = async () => {
    setLoading(true);
    try {
      const params = { limit: 50 };
      if (selectedCategory !== 'all') params.category = selectedCategory;
      if (searchQuery.trim()) params.search = searchQuery.trim();
      if (cityFilter.trim()) params.city = cityFilter.trim();
      const res = await axios.get(`${API_URL}/api/wishes`, { params });
      setWishes(res.data.wishes);
      setTotal(res.data.total);
    } catch {} finally { setLoading(false); }
  };

  const getCatName = (id) => { const c = categories.find(x => x.id === id); return c ? (it ? c.name_it : c.name_en) : id; };

  const handleContact = async (wish) => {
    try {
      const res = await axios.post(`${API_URL}/api/wishes/${wish.wish_id}/contact`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(it ? 'Messaggio inviato!' : 'Message sent!');
      navigate('/chat', { state: { conversationId: res.data.conversation_id } });
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const handleDelete = async (wish) => {
    if (!window.confirm(it ? 'Eliminare questo desiderio?' : 'Delete this wish?')) return;
    try {
      await axios.delete(`${API_URL}/api/wishes/${wish.wish_id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(it ? 'Eliminato' : 'Deleted');
      fetchWishes();
    } catch { toast.error('Error'); }
  };

  const enriched = wishes.map(w => ({ ...w, category_name: getCatName(w.category) }));

  return (
    <div className="min-h-screen bg-stone-100 dark:bg-stone-950 overflow-y-auto">
      <Navbar />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 pb-16">
        {/* Hero */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-br from-rose-600 to-pink-700 rounded-3xl p-6 md:p-10 mb-8 text-white relative overflow-hidden">
          <div className="absolute top-0 right-0 w-48 h-48 bg-rose-500/30 rounded-full -translate-y-1/2 translate-x-1/2" />
          <div className="absolute bottom-0 left-0 w-32 h-32 bg-pink-600/30 rounded-full translate-y-1/2 -translate-x-1/2" />
          <div className="relative z-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div>
              <div className="flex items-center gap-3 mb-3">
                <Sparkles className="h-7 w-7" />
                <span className="font-medium text-rose-200">{it ? 'Desideri del Cuore' : "Heart's Desires"}</span>
              </div>
              <h1 className="font-heading text-2xl md:text-3xl font-bold mb-2" data-testid="heart-desires-title">
                {it ? 'Esprimi un desiderio, qualcuno lo realizzerà' : 'Make a wish, someone will fulfill it'}
              </h1>
              <p className="text-rose-100 max-w-lg">
                {it ? 'Non servono soldi: a volte basta un gesto, un\'esperienza o un po\' di tempo per rendere felice qualcuno.' : "No money needed: sometimes a gesture, an experience, or a bit of time is enough to make someone happy."}
              </p>
            </div>
            {isAuthenticated && (
              <Dialog open={showCreate} onOpenChange={setShowCreate}>
                <DialogTrigger asChild>
                  <Button data-testid="create-wish-btn" className="bg-white text-rose-700 hover:bg-rose-50 rounded-full px-6 py-3 font-semibold shadow-lg shrink-0">
                    <Plus className="h-5 w-5 mr-2" />{it ? 'Esprimi un desiderio' : 'Make a wish'}
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="font-heading text-xl">{it ? 'Il tuo desiderio del cuore' : 'Your heart\'s desire'}</DialogTitle>
                  </DialogHeader>
                  <WishForm language={language} token={token} categories={categories}
                    onSuccess={() => { setShowCreate(false); fetchWishes(); }} />
                </DialogContent>
              </Dialog>
            )}
          </div>
        </motion.div>

        {/* Filters */}
        <div className="bg-white dark:bg-stone-800 rounded-2xl shadow-sm border border-stone-200 dark:border-stone-700 p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-3">
            <form onSubmit={e => { e.preventDefault(); fetchWishes(); }} className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
              <input data-testid="wish-search" value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                placeholder={it ? 'Cerca desideri...' : 'Search wishes...'}
                className="w-full pl-9 pr-4 py-2.5 bg-stone-50 dark:bg-stone-700 dark:text-white rounded-xl text-sm border border-stone-200 dark:border-stone-600 focus:outline-none focus:ring-2 focus:ring-rose-500/20" />
            </form>
            <div className="relative">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
              <input data-testid="wish-city-filter" value={cityFilter}
                onChange={e => setCityFilter(e.target.value)}
                onBlur={fetchWishes} onKeyDown={e => e.key === 'Enter' && fetchWishes()}
                placeholder={it ? 'Filtra per città...' : 'Filter by city...'}
                className="w-full md:w-[180px] pl-9 pr-4 py-2.5 bg-stone-50 dark:bg-stone-700 dark:text-white rounded-xl text-sm border border-stone-200 dark:border-stone-600 focus:outline-none focus:ring-2 focus:ring-rose-500/20" />
            </div>
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger data-testid="wish-category-filter" className="w-full md:w-[200px] rounded-xl">
                <Filter className="h-4 w-4 mr-2 text-stone-400" /><SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{it ? 'Tutte le categorie' : 'All categories'}</SelectItem>
                {categories.map(c => <SelectItem key={c.id} value={c.id}>{it ? c.name_it : c.name_en}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        <p className="text-stone-600 dark:text-stone-400 text-sm mb-4">{total} {it ? 'desideri trovati' : 'wishes found'}</p>

        {loading ? (
          <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-rose-600" /></div>
        ) : enriched.length === 0 ? (
          <div className="text-center py-20">
            <Sparkles className="h-16 w-16 text-stone-300 mx-auto mb-4" />
            <h3 className="font-heading text-xl font-semibold text-stone-700 dark:text-stone-300 mb-2" data-testid="no-wishes-message">
              {it ? 'Nessun desiderio ancora' : 'No wishes yet'}
            </h3>
            <p className="text-stone-500 mb-6">{it ? 'Sii il primo a esprimere un desiderio del cuore!' : "Be the first to express a heart's desire!"}</p>
            {isAuthenticated && (
              <Button onClick={() => setShowCreate(true)} className="bg-rose-600 hover:bg-rose-700 text-white rounded-full px-6">
                <Plus className="h-5 w-5 mr-2" />{it ? 'Esprimi un desiderio' : 'Make a wish'}
              </Button>
            )}
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-5">
            {enriched.map(wish => (
              <WishCard key={wish.wish_id} wish={wish} language={language}
                onContact={handleContact} onEdit={setEditingWish} onDelete={handleDelete}
                isAuthenticated={isAuthenticated} currentUserId={user?.user_id} />
            ))}
          </div>
        )}

        {/* Edit Dialog */}
        <Dialog open={!!editingWish} onOpenChange={(open) => { if (!open) setEditingWish(null); }}>
          <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">{it ? 'Modifica desiderio' : 'Edit wish'}</DialogTitle>
            </DialogHeader>
            {editingWish && (
              <WishForm language={language} token={token} categories={categories} editItem={editingWish}
                onSuccess={() => { setEditingWish(null); fetchWishes(); }} />
            )}
          </DialogContent>
        </Dialog>
      </main>
      <Footer />
    </div>
  );
};

export default HeartDesires;
